import React, { useEffect, useState } from "react";
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow } from "../components/Bits.jsx";

/** Staff scheduling: clinics, 1:1 blocks, pod crits. */
export default function Sessions() {
  const s = useSession();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ type: "clinic", title: "", startTime: "", durationMin: 60, capacity: 8, clusterId: "", podId: "", meetingUrl: "", guestName: "", guestBio: "" });
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!s.profile?.cohortId) return;
    const q = query(collection(db, "sessions"), where("cohortId", "==", s.profile.cohortId), orderBy("startTime", "asc"));
    return onSnapshot(q, (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [s.profile?.cohortId]);

  const create = async () => {
    if (!f.title.trim() || !f.startTime) return setMsg("Title and start time are required.");
    await addDoc(collection(db, "sessions"), {
      ...f,
      title: f.title.trim(),
      guestName: f.guestName?.trim() || null,
      guestBio: f.guestBio?.trim() || null,
      durationMin: Number(f.durationMin) || 60,
      capacity: Number(f.capacity) || null,
      clusterId: f.clusterId.trim() || null,
      podId: f.podId.trim() || null,
      meetingUrl: f.meetingUrl.trim() || null,
      cohortId: s.profile.cohortId,
      hostUid: s.user.uid,
      hostName: s.profile?.name ?? null,
      attendeeUids: [],
      startTime: new Date(f.startTime).toISOString(),
    });
    setF({ ...f, title: "", startTime: "", meetingUrl: "" });
    setMsg("Posted. Residents can book it from their Help tab.");
  };

  const input = (k, ph, type = "text", w) => (
    <input type={type} placeholder={ph} value={f[k]} style={{ width: w }} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
  );

  return (
    <div style={{ maxWidth: 900 }}>
      <Eyebrow>Sessions</Eyebrow>
      <h1 style={{ fontSize: 24, marginTop: 4 }}>Clinics, 1:1s, and crits</h1>
      <p style={{ color: "var(--graphite)", maxWidth: "66ch" }}>
        The weekly minimum per cluster: one Clinic, one 1:1 block, and each pod's crit. Once a month,
        a Visiting Critic sits a crit — every visitor is a Jury candidate and a hiring pipeline; log them
        as such. A cluster whose clinics are full every week needs another Studio Master, not more seats.
      </p>

      <div className="card" style={{ margin: "14px 0" }}>
        <Eyebrow>Post a session</Eyebrow>
        <div className="row wrapflex" style={{ gap: 8, marginTop: 8 }}>
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} style={{ width: 120 }}>
            <option value="clinic">Clinic</option>
            <option value="oneonone">1:1 block</option>
            <option value="crit">Pod crit</option>
            <option value="visiting">Visiting Critic</option>
          </select>
          {input("title", "Title / topic", "text", 240)}
          {input("startTime", "", "datetime-local", 220)}
          {input("durationMin", "Minutes", "number", 90)}
          {input("capacity", "Capacity", "number", 90)}
        </div>
        <div className="row wrapflex" style={{ gap: 8, marginTop: 8 }}>
          {input("clusterId", "Cluster scope (optional)", "text", 200)}
          {input("podId", "Pod scope (crits)", "text", 160)}
          {input("meetingUrl", "Meeting URL (shown to booked residents)", "text", 320)}
        </div>
        {f.type === "visiting" && (
          <div className="row wrapflex" style={{ gap: 8, marginTop: 8 }}>
            {input("guestName", "Visiting critic's name", "text", 220)}
            {input("guestBio", "One line: who they are and where they practise", "text", 380)}
          </div>
        )}
        <div className="row wrapflex" style={{ gap: 8, marginTop: 8 }}>
          <button className="btn" onClick={create}>Post</button>
        </div>
        {msg && <p style={{ marginTop: 8, fontSize: 13 }}>{msg}</p>}
      </div>

      <table>
        <thead><tr><th>When</th><th>Type</th><th>Title</th><th>Scope</th><th>Booked</th><th></th></tr></thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.id}>
              <td style={{ fontSize: 13 }}>{new Date(x.startTime).toLocaleString()}</td>
              <td>{x.type}</td>
              <td>{x.title}</td>
              <td className="mono" style={{ fontSize: 12 }}>{x.podId ?? x.clusterId ?? "cohort"}</td>
              <td className="mono">{(x.attendeeUids ?? []).length}{x.capacity ? `/${x.capacity}` : ""}</td>
              <td>{s.isSenior && <button className="btn ghost" onClick={() => deleteDoc(doc(db, "sessions", x.id))}>Delete</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
